import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { oneformBFFNdpsYumiwiSend } from 'src/bff';
import {
	DetalleMotivo,
	MultipleCTASelection,
	MultipleOptionCTAs,
	Telefono,
} from 'src/components';
import {
	FormController,
	LayoutTemplate,
	LayoutWrapper,
	useOneFormContext,
} from 'src/feature-app';
import {
	CitaCEMSegundaEntrega,
	ConfirmacionCEMSegundaEntrega,
	DealerCEMSegundaEntrega,
	getCommonFormData,
	ThankYouCEMSegundaEntrega,
} from 'src/forms/Cem/CEMSegundaEntrega';
import { sanitizeKvps } from 'src/forms/Cem/helpers/data';
import { formDataBuilder } from 'src/forms/format-fields';
import {
	addRemovedStep,
	getIndexInSteps,
	removeStep,
	resetStepsScreenIndex,
} from 'src/forms/helpers/add-remove-steps';
import { logMessage, transformDate } from 'src/helpers';
import {
	FormDataGroup,
	FormInfo,
	IdDealer,
	IdOrder,
	IdPerson,
	IdVehicle,
	OneFormState,
	Steps,
} from 'src/types';

export interface CEMSegundaEntregaFormProps {
	dealers?: IdDealer[];
	idPerson: IdPerson;
	idVehicle: IdVehicle;
	idDealer: IdDealer;
	idOrder: IdOrder;
}

export function CEMSegundaEntregaForm(props: CEMSegundaEntregaFormProps) {
	const { dealers, idPerson, idVehicle, idDealer, idOrder } = props;
	const { initializeForm, handleNextStep } = useOneFormContext();
	const { formData } = useSelector((state: OneFormState) => state);
	const store = useStore();
	const dispatch = useDispatch();

	// Definición de steps
	const isElectrico = idVehicle.VGED_ModelGroup__c?.substring(0, 2) == 'ID';

	const CTAsTipoConsulta = [
		{
			label: 'Acabados y funcionalidades',
			value: 'Acabados y funcionalidades',
		},
		{ label: 'Ayuda a la conducción', value: 'Ayuda a la conducción' },
		{
			label: isElectrico ? 'Carga y autonomía' : 'Consumo de combustible',
			value: isElectrico ? 'Carga y autonomía' : 'Consumo de combustible',
		},
		{
			label: 'Conexión a internet y apps',
			value: 'Conexión a internet y apps',
		},
		{
			label: 'Garantía y mantenimiento',
			value: 'Garantía y mantenimiento',
		},
		{ label: 'Otros', value: 'Otros' },
	];

	const CTAsTipoContacto = [
		{ label: 'Llamada', value: 'Teléfono' },
		{ label: 'Videollamada', value: 'Videollamada' },
		{ label: 'Visita en el punto de venta', value: 'Visita' },
	];

	let index = 0;
	const defaultSteps: Steps[] = [
		{
			title: (
				<>
					<Text bold>¿Sobre qué </Text>
					<Text>quieres hablar con tu experto?</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleOptionCTAs
							ctaValues={CTAsTipoConsulta}
							name="tipoConsulta"
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'TipoConsulta',
			outputs: ['tipoConsulta'],
			outputsText: ['tipoConsulta'],
		},
		{
			title: (
				<>
					<Text>¿Nos das </Text>
					<Text bold>más detalles?</Text>
					<span style={{ whiteSpace: 'nowrap' }}>
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<DetalleMotivo
							label="Escríbelo aquí"
							name="detalleConsulta"
							numberOfMaxLength={300}
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'DetalleConsulta',
			outputs: ['detalleConsulta'],
			outputsText: ['detalleConsulta'],
		},
		{
			title: (
				<>
					<Text bold>¿Cómo </Text>
					<Text>prefieres que sea la cita?</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleCTASelection
							ctaValues={CTAsTipoContacto}
							name="tipoContacto"
							nextOnClick
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'TipoContacto',
			outputs: ['tipoContacto'],
			outputsText: ['tipoContacto'],
			ctas: CTAsTipoContacto,
		},
		{
			title: (
				<>
					{idPerson?.FirstName}, ¿nos podrías confirmar tus{' '}
					<Text bold>datos de contacto?</Text>
				</>
			),
			fields: (
				<LayoutWrapper>
					<Telefono required={true} />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'ConfirmacionTelefono',
			outputs: ['cellphone'],
			outputsText: ['cellphone'],
		},
		dealers
			? {
					title: (
						<>
							<Text bold>¿A qué punto de venta</Text> quieres ir?
						</>
					),
					fields: (
						<DealerCEMSegundaEntrega
							name="seleccionDealer"
							dealers={dealers}
						/>
					),
					screenIndex: index++,
					name: 'SeleccionDealer',
			  }
			: null,
		dealers
			? {
					title: (
						<>
							<Text bold>¿Y cuándo </Text>
							<Text>te va mejor?</Text>
						</>
					),
					fields: (
						<CitaCEMSegundaEntrega
							fechaName="fechaCita"
							horaName="horaCita"
						/>
					),
					screenIndex: index++,
					name: 'Calendario',
					outputs: ['fechaCita', 'horaCita'],
					outputsText: ['fechaCita', 'horaCita'],
			  }
			: null,
		{
			fields: <ConfirmacionCEMSegundaEntrega idPerson={idPerson} />,
			screenIndex: index++,
			name: 'Confirmacion',
			outputs: ['confirmacion'],
			outputsText: ['confirmacion'],
		},
	];

	const filteredSteps = defaultSteps.filter(step => step !== null);

	// Lógica para:
	// añadir DetalleConsulta si TipoConsulta contiene Otros
	// añadir ConfirmacionTelefono si TipoContacto es Teléfono o Videollamada

	const { tipoConsulta, tipoContacto } = formData.fields;

	useEffect(() => {
		if (tipoConsulta) {
			updateSteps('DetalleConsulta', tipoConsulta.includes('Otros'));
		}
		if (tipoContacto) {
			updateSteps(
				'ConfirmacionTelefono',
				tipoContacto == 'Teléfono' || tipoContacto == 'Videollamada',
			);
		}
	}, [tipoConsulta, tipoContacto]);

	const updateSteps = (stepName: string, conditional: boolean) => {
		const { steps: actualSteps, screenIndex } = store.getState().formInfo;
		let newSteps = [...actualSteps];
		if (conditional) {
			const stepIndex = getIndexInSteps(newSteps, stepName);

			if (stepIndex === -1) {
				newSteps = addRemovedStep(
					filteredSteps,
					actualSteps,
					stepName,
					screenIndex,
				);
				setTimeout(() => {
					handleNextStep();
				}, 0);
			}
		} else {
			newSteps = removeStep(newSteps, stepName);
		}

		const newStepsWithIndex = resetStepsScreenIndex(newSteps);

		dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
		dispatch({
			type: 'UPDATE_LAST_STEP',
			payload: newStepsWithIndex[newStepsWithIndex.length - 1],
		});
		dispatch({
			type: 'SET_NUMBER_OF_SCREENS',
			payload: newStepsWithIndex.length,
		});
	};

	// Datos de envío
	const CEMSegundaEntregaData: Partial<FormDataGroup> = {
		headers: {},
		fields: {
			name: idPerson.FirstName,
			surname: idPerson.LastName,
			secondSurname: idPerson.MiddleName,
			email: idPerson.PersonEmail,
			cellphone: idPerson.PersonMobilePhone,
			formName: 'cem-segunda_entrega-invitacion',
			formType: 'otherform',
			pageCategory: 'Encuesta',
		}, // Analítica
	};

	useEffect(() => {
		logMessage(getFormData());
	}, [formData]);

	const getFormData = () => {
		const { formData, dealersInfo }: OneFormState = store.getState();
		const { fields } = formData;
		const { selectedInstallation } = dealersInfo;

		const data = getCommonFormData(idPerson, idVehicle, idDealer, idOrder);

		data['fields[SURVEY_ANSWER_01]'] = 'true';
		data['fields[SURVEY_ANSWER_02]'] = fields.tipoConsulta;
		data['fields[SURVEY_ANSWER_03]'] = fields.detalleConsulta
			? fields.detalleConsulta
			: '';
		data['fields[SURVEY_ANSWER_04]'] = fields.tipoContacto;
		data['fields[APPOINTMENT_DATE]'] =
			transformDate(fields.fechaCita, 'DD/MM/YYYY') +
			' ' +
			fields.horaCita +
			':00';
		data['fields[PERSON_RGPD_PERFILADO]'] = fields.tmk
			? fields.tmk
			: 'NO INFORMADO';
		data['fields[PERSON_RGPD_COM_COMERCIALES]'] = fields.lssi
			? fields.lssi
			: 'NO INFORMADO';

		if (fields.cellphone) {
			data['fields[PERSON_PHONE_MOBILE]'] = fields.cellphone;
		}

		if (selectedInstallation) {
			data['fields[SF_LEAD_INSTALLATION_ID]'] = selectedInstallation.Id;
			data['fields[SF_LEAD_INSTALLATION_NAME]'] =
				selectedInstallation.VGED_CommercialName__c;
			data['fields[SF_LEAD_INSTALLATION_CODE]'] =
				selectedInstallation.VGED_DealerCode__c;
			data['fields[SF_LEAD_INSTALLATION_CODE_KVPS]'] = sanitizeKvps(
				selectedInstallation.KVPSCode__c,
			);
		}

		data['PERSON_ID'] = idPerson.Id;

		return data;
	};

	const sendForm = (): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const dataStringified = JSON.stringify(getFormData());
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsYumiwiSend(formData);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	// Inicialización del form

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: filteredSteps[0],
			lastStep: filteredSteps[filteredSteps.length - 1],
			stepsHistory: [0],
			numberOfScreens: filteredSteps.length,
			newSteps: null,
		};
	};

	useEffect(() => {
		const { firstStep, lastStep, stepsHistory, numberOfScreens, newSteps } =
			getFormInfo();

		const CEMSegundaEntregaInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens,
			screenIndex: firstStep.screenIndex,
			formTheme: 'main',
			notALayer: true,
			formType: 'inPage',
			step: firstStep,
			steps: newSteps ? newSteps : filteredSteps,
			stepsHistory,
			thankYouComponent: (
				<ThankYouCEMSegundaEntrega idVehicle={idVehicle} />
			),
		};
		initializeForm(CEMSegundaEntregaInfo, CEMSegundaEntregaData);
	}, [dealers]);

	return filteredSteps ? (
		<FormController steps={filteredSteps} screenType="full-screen" />
	) : null;
}
